import { useState } from 'react';
import type React from 'react';
import { portalOrderService } from '../../services/portalOrders';
import type { BolScanDeskAnswer, BolScanDeskAskInfo, BolScanResubmitInfo } from '../../services/portalOrders';

/**
 * Banner for a scanned BOL waiting on the order desk (kanban cee85483).
 *
 * Two asks the warehouse gun used to cover silently: the sales site wants a
 * PRO / pickup number to finish the ship, or the portal held the page until
 * someone confirms the order left (its ship date is outside the window, or the
 * page was matched by its contents instead of an order barcode; kanban 51f7a86d).
 * The CSR answers here; each answer is its own audited scan row on the server.
 */
interface Props {
  orderId: string;
  ask: BolScanDeskAskInfo;
  canEdit: boolean;
  /** Optional hook for the server's answer (the banner shows the outcome itself). */
  onAnswered?: (result: BolScanResubmitInfo) => void;
}

const PRIMARY: React.CSSProperties = {
  padding: '4px 12px',
  borderRadius: 4,
  border: '1px solid #b45309',
  background: '#b45309',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
const SECONDARY: React.CSSProperties = { ...PRIMARY, background: '#fff', color: '#92400e' };

function errorText(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Could not reach the sales site. Try again.';
}

export default function BolScanDeskAskBanner({ orderId, ask, canEdit, onAnswered }: Props) {
  const [pro, setPro] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Answered for good (shipped / already shipped / settled): only the outcome stays.
  const [settled, setSettled] = useState(false);

  const send = async (body: BolScanDeskAnswer) => {
    setBusy(true);
    setError(null);
    try {
      const result = await portalOrderService.answerBolScanDeskAsk(orderId, body);
      setMessage(result.message);
      setSettled(result.status !== 'needs_input' && result.status !== 'failed');
      onAnswered?.(result);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const isPro = ask.ask === 'tracking_no';
  const isContentLink = !isPro && ask.needs_reason === 'content_link';
  const heading = isPro
    ? 'PRO number needed to finish this shipment.'
    : isContentLink
      ? 'BOL matched by content: confirm it shipped.'
      : 'BOL scanned: confirm it shipped.';
  return (
    <div
      data-testid="bol-scan-desk-ask-banner"
      data-section="bol_scan_desk_ask"
      data-ask={ask.ask}
      data-needs-reason={ask.needs_reason ?? undefined}
      data-target-order-id={orderId}
      role="alert"
      style={{
        margin: '6px 0 10px',
        padding: '10px 12px',
        borderRadius: 6,
        background: '#fffbeb',
        border: '1px solid #fcd34d',
        color: '#92400e',
        fontSize: '13px',
        lineHeight: 1.45,
      }}
    >
      {settled ? (
        <strong>{ask.sage_order_no}: answered.</strong>
      ) : (
        <>
          <strong>{heading}</strong> {ask.sage_order_no}: {ask.why}.
        </>
      )}
      {canEdit && !settled && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
          {isPro ? (
            <>
              <input
                aria-label="PRO or pickup number"
                placeholder="PRO or pickup number"
                value={pro}
                maxLength={64}
                onChange={(e) => setPro(e.target.value)}
                disabled={busy}
                style={{ padding: '4px 8px', border: '1px solid #d97706', borderRadius: 4, fontSize: 13 }}
              />
              <button
                type="button"
                data-action="bol_scan_pro_submit"
                data-outcome="request"
                data-target-order-id={orderId}
                disabled={busy || !pro.trim()}
                style={{ ...PRIMARY, opacity: busy || !pro.trim() ? 0.5 : 1 }}
                onClick={() => send({ action: 'tracking_no', tracking_no: pro.trim() })}
              >
                Finish ship
              </button>
              <button
                type="button"
                data-action="bol_scan_ship_without_pro"
                data-outcome="request"
                data-target-order-id={orderId}
                disabled={busy}
                style={SECONDARY}
                onClick={() => send({ action: 'skip_tracking' })}
              >
                Ship without one
              </button>
            </>
          ) : (
            <button
              type="button"
              data-action="bol_scan_confirm_shipped"
              data-outcome="request"
              data-target-order-id={orderId}
              disabled={busy}
              style={{ ...PRIMARY, opacity: busy ? 0.5 : 1 }}
              onClick={() => send({ action: 'confirm_shipped' })}
            >
              Confirm it shipped
            </button>
          )}
          {busy && <span>Sending to the sales site…</span>}
        </div>
      )}
      {message && <div data-testid="bol-scan-desk-ask-result" style={{ marginTop: 6 }}>{message}</div>}
      {error && (
        <div data-testid="bol-scan-desk-ask-error" style={{ marginTop: 6, color: '#991b1b' }}>
          {error}
        </div>
      )}
    </div>
  );
}
